import { useState } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Checkbox, FormControlLabel, FormGroup, Link, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { getCompanyInfo } from '../../../api/company';
import { registerUser } from '../../../api/register';
import UserImage from '../../../assets/images/pages/register/user.png';
import DialogLoading from '../../../components/feedback/DialogLoading';
import RegisterFooter from '../../../components/layouts/RegisterFooter';
import RegisterLayout from '../../../components/layouts/RegisterLayout';
import AppButton from '../../../components/ui/AppButton';
import AppInput from '../../../components/ui/AppInput';
import ErrorMessage from '../../../components/ui/ErrorMessage';
import { useDialogStore } from '../../../stores/dialogStore';
import { useUserStore } from '../../../stores/userStore';

const RegisterForm = (props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const openDialog = useDialogStore((state) => state.openDialog);
  const companyInfo = useUserStore((state) => state.companyInfo);
  const setCompanyInfo = useUserStore((state) => state.setCompanyInfo);

  const [dialogLoadingModal, setDialogLoadingModal] = useState(false);
  const [disabledPeopleName, setDisabledPeopleName] = useState(false);

  const isShowPeopleName =
    companyInfo && Object.keys(companyInfo).length > 0 && !companyInfo?.user_id;

  const formik = useFormik({
    initialValues: {
      companyCode: '',
      companyName: '',
      peopleName: '',
      acceptConsent: false,
    },
    validationSchema: Yup.object({
      companyCode: Yup.string().required('กรุณากรอกรหัสบริษัท'),
      companyName: Yup.string().required('กรุณากรอกชื่อบริษัท'),
      peopleName: isShowPeopleName
        ? Yup.string().required('กรุณากรอกชื่อ-นามสกุล ผู้มีอำนาจ')
        : Yup.string(),
      acceptConsent: Yup.boolean().oneOf([true], 'กรุณายอมรับข้อตกลง'),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      try {
        setDialogLoadingModal(true);

        const dto = {
          code_company: values.companyCode,
          people_name: values.peopleName,
          phonenumber: props.phoneNumber,
        };

        const response = await registerUser(dto);

        if (response.Code === '20100') {
          queryClient.invalidateQueries({ queryKey: ['userList'] });
          queryClient.invalidateQueries({ queryKey: ['companyList'] });
          navigate('/home', { replace: true });
        }
      } catch (e) {
        console.error(e);
        openDialog({
          title: 'เกิดข้อผิดพลาด',
          message: 'ไม่สามารถสมัครสมาชิกได้',
          variant: 'error',
        });
      } finally {
        setDialogLoadingModal(false);
      }
    },
  });

  const fetchCompanyInfo = async (code) => {
    try {
      const response = await getCompanyInfo({ code });

      if (response.Code === '20000') {
        const companyData = response.Data.data[0];
        formik.setFieldValue('companyName', companyData.company_name);

        if (companyData.people_name) {
          formik.setFieldValue('peopleName', companyData.people_name);
          setDisabledPeopleName(true);
        }

        setCompanyInfo(companyData);
      }
    } catch (e) {
      console.error(e);
      openDialog({
        title: 'เกิดข้อผิดพลาด',
        message: 'ไม่สามารถค้นหาข้อมูลบริษัทได้',
        variant: 'error',
      });
    }
  };

  const onCompanyCodeChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue('companyCode', value);

    if (_.size(value) === 10) fetchCompanyInfo(value);
  };

  return (
    <RegisterLayout>
      <Box sx={{ pt: 1, px: 2, pb: 4, position: 'relative', zIndex: '10' }}>
        {/* Title */}
        <Typography
          component="h1"
          sx={{
            fontSize: '2.25rem',
            fontWeight: '700',
            lineHeight: 1,
            color: '#005972',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          โปรดยืนยันข้อมูลของท่าน
        </Typography>

        {/* Form */}
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <Box>
              <AppInput
                name="companyCode"
                placeholder="รหัสบริษัท"
                value={formik.values.companyCode}
                inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={onCompanyCodeChange}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid white',
                  },
                }}
              />
              {formik.errors.companyCode && <ErrorMessage message={formik.errors.companyCode} />}
            </Box>
            <Box>
              <AppInput
                name="companyName"
                placeholder="ชื่อบริษัท"
                value={formik.values.companyName}
                onChange={formik.handleChange}
                disabled
                sx={{
                  '& .MuiOutlinedInput-notchedOutline, & .Mui-disabled .MuiOutlinedInput-notchedOutline':
                    {
                      border: '1px solid white',
                    },

                  '& .Mui-disabled.MuiInputBase-input': {
                    color: '#004559',
                    WebkitTextFillColor: '#004559',
                  },
                }}
              />
              {formik.errors.companyName && <ErrorMessage message={formik.errors.companyName} />}
            </Box>
            {isShowPeopleName && (
              <Box>
                <AppInput
                  name="peopleName"
                  value={formik.values.peopleName}
                  onChange={formik.handleChange}
                  disabled={disabledPeopleName}
                  placeholder="ชื่อ-นามสกุล ผู้มีอำนาจ"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid white',
                    },
                  }}
                />
                {formik.errors.peopleName && <ErrorMessage message={formik.errors.peopleName} />}
              </Box>
            )}

            <Box sx={{ marginTop: '10px' }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="acceptConsent"
                      checked={formik.values.acceptConsent}
                      onChange={formik.handleChange}
                      icon={
                        <CheckBoxOutlineBlankIcon
                          sx={{
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            border: 'none',
                            '& path': {
                              display: 'none',
                            },
                          }}
                        />
                      }
                      checkedIcon={
                        <CheckBoxIcon
                          sx={{
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            color: '#005972',
                          }}
                        />
                      }
                    />
                  }
                  label={
                    <span>
                      ฉันยอมรับ
                      <Link
                        href="https://s3.starcardclub.com/starcardclub/%E0%B8%99%E0%B9%82%E0%B8%A2%E0%B8%9A%E0%B8%B2%E0%B8%A2%E0%B9%80%E0%B8%81%E0%B8%B5%E0%B9%88%E0%B8%A2%E0%B8%A7%E0%B8%81%E0%B8%B1%E0%B8%9A%E0%B8%81%E0%B8%B2%E0%B8%A3%E0%B8%84%E0%B8%B8%E0%B9%89%E0%B8%A1%E0%B8%84%E0%B8%A3%E0%B8%AD%E0%B8%87%E0%B8%82%E0%B9%89%E0%B8%AD%E0%B8%A1%E0%B8%B9%E0%B8%A5%E0%B8%AA%E0%B9%88%E0%B8%A7%E0%B8%99%E0%B8%9A%E0%B8%B8%E0%B8%84.pdf"
                        target="_blank"
                        rel="noopener"
                        underline="always"
                        sx={{ textDecorationColor: '#005972' }}
                      >
                        เงื่อนไขการใช้บริการและนโยบายความเป็นส่วนตัว
                      </Link>
                    </span>
                  }
                  sx={{
                    '&.MuiFormControlLabel-root': {
                      margin: '0',
                      gap: '.25rem',
                    },
                    '& .MuiTypography-root': {
                      fontSize: '1rem',
                      color: '#005972',
                    },
                  }}
                />
              </FormGroup>
              <AppButton
                type="submit"
                fullWidth
                disabled={!formik.values.acceptConsent}
                endIcon={<ChevronRightIcon sx={{ fontSize: '2rem' }} />}
                variant="brand"
                sx={{
                  mt: 2,
                  '&.Mui-disabled': {
                    color: 'white',
                    backgroundColor: '#356470ad',
                  },
                }}
              >
                ลงทะเบียน
              </AppButton>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Background */}
      <Box
        component={'img'}
        src={UserImage}
        alt="User"
        sx={{
          width: '100%',
          position: 'absolute',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          maxHeight: 400,
          objectFit: 'cover',
          objectPosition: '0 0',
          maxWidth: 'md',
        }}
      />
      {/* Footer */}
      <RegisterFooter />
      <DialogLoading open={dialogLoadingModal} message="กำลังสมัครสมาชิก" />
    </RegisterLayout>
  );
};

export default RegisterForm;
